import { FBCLID_PROVENANCE_PARAM } from '@shared/marketing/click-provenance'
import { FINAL_URL_PARAM } from '@shared/marketing/config'

/** Optional context only: the backend alone determines conversion facts and identity. */
export const META_CONTEXT_HEADER = 'X-Podo-Meta-Context'
const CLICK_STORAGE_KEY = 'podo_meta_click_v1'
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const CONTROL = /[\u0000-\u001f\u007f]/
const CLICK = /^[A-Za-z0-9_-]{1,500}$/
const FBP_COOKIE = /^fb\.\d\.\d{10,16}\.\d{1,100}$/
const FBC_COOKIE = /^fb\.\d\.\d{10,16}\.[A-Za-z0-9_-]{1,500}$/

// Failure markers contain no click, user or attribution values. Recovery must not
// turn an already observed-but-unpersisted click into a new click on the same page.
const unreliableClickStorage = new WeakSet<object>()
const unreliableClickWindows = new WeakSet<object>()

export interface MetaContext {
  v: 1
  country: 'KR'
  platform: 'WEB'
  collectedAt: number
  sourceUrl: string
  userAgent: string
  trackingAllowed: true
  fbp?: string
  fbc?: string
  fbclid?: string
  fbclidObservedAt?: number
}

export interface MetaCaptureInput {
  captureEnabled: boolean
  privacyApproved: boolean
  country: string | null | undefined
  native: boolean
  globalPrivacyControl?: boolean
  doNotTrack?: string | null
  href: string
  userAgent: string
  cookies: string
  now: number
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  allowNewClickObservation?: boolean
}

export function captureMetaContext(input: MetaCaptureInput): MetaContext | undefined {
  try {
    if (
      !input.captureEnabled ||
      !input.privacyApproved ||
      input.country !== 'KR' ||
      input.native ||
      input.globalPrivacyControl ||
      input.doNotTrack === '1' ||
      input.doNotTrack === 'yes'
    ) {
      return undefined
    }
    const url = new URL(input.href)
    if (
      !['https:', 'http:'].includes(url.protocol) ||
      url.username ||
      url.password ||
      CONTROL.test(input.href) ||
      !input.userAgent ||
      input.userAgent.length > 1024 ||
      CONTROL.test(input.userAgent)
    ) {
      return undefined
    }
    const context: MetaContext = {
      v: 1,
      country: 'KR',
      platform: 'WEB',
      collectedAt: input.now,
      sourceUrl: url.origin + url.pathname,
      userAgent: input.userAgent,
      trackingAllowed: true,
    }
    if (context.sourceUrl.length > 2048) {
      return undefined
    }
    for (const [cookie, field] of [
      ['_fbp', 'fbp'],
      ['_fbc', 'fbc'],
    ] as const) {
      const raw = input.cookies
        .split(';')
        .map((part) => part.trim())
        .find((part) => part.startsWith(cookie + '='))
      const value = raw ? decodeURIComponent(raw.slice(cookie.length + 1)) : undefined
      if (value && (field === 'fbp' ? FBP_COOKIE : FBC_COOKIE).test(value)) {
        context[field] = value
      }
    }
    // Legacy marketing cookies have no observation timestamp: never infer one from conversion time.
    let observed: { value: string; at: number } | undefined
    let observations: { value: string; at: number }[] = []
    const knownObservation = (item: { value: string; at: number }) =>
      item && typeof item.value === 'string' && CLICK.test(item.value) && Number.isSafeInteger(item.at) && item.at > 0
    const recentObservation = (item: { value: string; at: number } | undefined) =>
      item && item.at <= input.now && input.now - item.at <= MAX_AGE_MS ? item : undefined
    let storage: MetaCaptureInput['storage']
    let storageReadable = false
    try {
      storage = input.storage
      const raw = storage?.getItem(CLICK_STORAGE_KEY)
      const saved = raw ? JSON.parse(raw) : null
      storageReadable = Boolean(storage)
      // Keep bounded expired identities as tombstones: discarding them would re-date the same URL click.
      observations = Array.isArray(saved?.observations) ? saved.observations.filter(knownObservation).slice(0, 20) : []
      observed = recentObservation(observations.find((item) => item.value === saved?.current))
      if (raw && !observations.length) {
        storage?.removeItem(CLICK_STORAGE_KEY)
      }
    } catch {
      storageReadable = false
      if (storage) {
        unreliableClickStorage.add(storage)
      }
      /* Storage is optional; cookies can still supply attribution. */
    }
    const click = url.searchParams.get('fbclid')
    if (click && CLICK.test(click)) {
      const known = observations.find((item) => item.value === click)
      observed = recentObservation(known)
      // finalUrl remains a legacy restoration guard; new producers carry an explicit, non-cookie marker.
      if (
        storageReadable &&
        storage &&
        !unreliableClickStorage.has(storage) &&
        input.allowNewClickObservation !== false &&
        !known &&
        !url.searchParams.has(FBCLID_PROVENANCE_PARAM) &&
        url.searchParams.get(FINAL_URL_PARAM) !== 'true' &&
        observations.length < 20
      ) {
        observed = { value: click, at: input.now }
        observations.push(observed)
      }
      if (storageReadable) {
        try {
          const snapshot = JSON.stringify({ current: observed?.value, observations })
          storage?.setItem(CLICK_STORAGE_KEY, snapshot)
          if (!known && storage?.getItem(CLICK_STORAGE_KEY) !== snapshot) {
            if (storage) {
              unreliableClickStorage.add(storage)
            }
            observed = undefined
          }
        } catch {
          if (storage) {
            unreliableClickStorage.add(storage)
          }
          // Only an observation already read from storage has a verified original time.
          observed = recentObservation(known)
        }
      }
    }
    if (!context.fbc && observed) {
      context.fbclid = observed.value
      context.fbclidObservedAt = observed.at
      // The backend constructs fbc from this original observation, never from send time.
    }
    return context
  } catch {
    return undefined
  }
}

export function encodeMetaContext(context: MetaContext): string | undefined {
  try {
    const binary = Array.from(new TextEncoder().encode(JSON.stringify(context)), (byte) =>
      String.fromCharCode(byte),
    ).join('')
    const value = btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
    return value.length <= 8192 ? value : undefined
  } catch {
    return undefined
  }
}

export function getMetaContextHeaders(country: string | null = null): Record<string, string> {
  try {
    if (typeof window === 'undefined') {
      return {}
    }
    // These are deliberately separate, default-OFF build gates. PRE1 must settle consent/withdrawal before enabling.
    const captureEnabled = process.env.NEXT_PUBLIC_META_CAPI_CAPTURE_ENABLED === 'true'
    const privacyApproved = process.env.NEXT_PUBLIC_META_CAPI_PRIVACY_APPROVED === 'true'
    if (!captureEnabled || !privacyApproved) {
      return {}
    }
    const nav = navigator as Navigator & { globalPrivacyControl?: boolean }
    if (nav.globalPrivacyControl || nav.doNotTrack === '1' || nav.doNotTrack === 'yes') {
      try {
        window.sessionStorage.removeItem(CLICK_STORAGE_KEY)
      } catch {
        /* Optional. */
      }
      return {}
    }
    let storage: MetaCaptureInput['storage']
    try {
      storage = window.sessionStorage
    } catch {
      unreliableClickWindows.add(window)
      /* Keep cookies and general context. */
    }
    if (!storage) {
      unreliableClickWindows.add(window)
    }
    const context = captureMetaContext({
      captureEnabled,
      privacyApproved,
      country,
      native: Boolean(window.ReactNativeWebView) || /PodoApp\/RN/i.test(nav.userAgent),
      globalPrivacyControl: nav.globalPrivacyControl,
      doNotTrack: nav.doNotTrack,
      href: window.location.href,
      userAgent: nav.userAgent,
      cookies: document.cookie,
      now: Date.now(),
      storage,
      allowNewClickObservation: !unreliableClickWindows.has(window),
    })
    const encoded = context && encodeMetaContext(context)
    return encoded ? { [META_CONTEXT_HEADER]: encoded } : {}
  } catch {
    return {}
  }
}

/** BFF forwards only a bounded opaque header; authoritative validation remains in backend. */
export function forwardMetaContextHeaders(
  headers: Pick<Headers, 'get'>,
  isNative = false,
  bffOrigin?: string,
): Record<string, string> {
  if (
    isNative ||
    process.env.NEXT_PUBLIC_META_CAPI_CAPTURE_ENABLED !== 'true' ||
    process.env.NEXT_PUBLIC_META_CAPI_PRIVACY_APPROVED !== 'true' ||
    headers.get('Sec-GPC') === '1' ||
    headers.get('DNT') === '1' ||
    /PodoApp\/RN/i.test(headers.get('User-Agent') ?? '')
  ) {
    return {}
  }
  const value = headers.get(META_CONTEXT_HEADER)
  if (!value || value.length > 8192 || !/^[A-Za-z0-9_-]+$/.test(value)) {
    return {}
  }
  const forwarded: Record<string, string> = { [META_CONTEXT_HEADER]: value }
  for (const name of ['User-Agent', 'Origin', 'DNT', 'Sec-GPC']) {
    const original = headers.get(name)
    if (original) {
      forwarded[name] = original
    }
  }
  // Browser GET omits Origin. The BFF's configured public origin, not a client context field, identifies it.
  if (!forwarded.Origin && bffOrigin) {
    forwarded.Origin = bffOrigin
  }
  return forwarded
}
